import React, { useState, useEffect, useMemo, useRef } from "react"
import { useSelector, useDispatch } from "react-redux"
import makeStyles from "@mui/styles/makeStyles"
import Fab from "@mui/material/Fab"
import { Box } from "@mui/material"
import Add from "@mui/icons-material/Add"

import {
  createProductResume,
  showRecipeTab,
  showAdvancedSearch,
  loadAdditionalFiltersForRecipes,
  refreshRecipesForAdditionalFilters,
} from "../../actions/Recipe/recipes"
import { loadEnterprises } from "../../actions/Organizations/Enterprise"

import { getRecipeStatusToString, RecipeStatus } from "../../utils/recipes"
import { getBrandBy, ACTIVE_KFC_BRANDS } from "../../utils"
import { getOrganizationsEnterprisesSelector } from "../../reducers/Organizations/organizations"
import { generateCloudinaryDownloadUrl } from "../../utils/cloudinary"
import { getProductTypeOptionsSelector } from "../../reducers/ProductTypes/productTypes"
import { getProductTypeLabelByProduct } from "../../utils/productTypesUtils"
import { getRecipes } from "../../reducers/Recipe/recipes"
import { getCommercialNamesOptions } from "../../reducers/Ingredients/Internal/CommercialName"
import { getSupplierItemsOptionsSelector } from "../../reducers/Suppliers/SupplierItems/supplierItems"
import { getProductTagsOptionsSelector } from "../../reducers/ProductsTags/ProductsTags"
import DataGrid from "../../components/DataGrid/DataGrid"
import GridThumbnail from "../../components/DataGrid/GridThumbnail"
import ProductResumeCreate from "../../components/Recipe/ProductResumeCreate"
import ReportOrderRDDialog from "./orderReportRD/ReportOrderRDDialog"
import BooleanIcons from "../../components/BooleanIcons"
import moment from "moment"
import { TableDateFormat } from "../../components/table-utils/utils"
import { RECIPE_DATAGRID_STORAGE } from "../../utils/constant"

// --- pure helpers (no component state) ---

const formatRecipeSupplierItems = (ingredients = []) =>
  ingredients.map(i => i.supplierItem ? `${i.supplierItem.objectId} - ${i.supplierItem.name}` : "")

const formatRecipeInternalTags = (tags = []) =>
  tags.map(t => t.name)

const formatRecipeCommercialNames = (ingredients = []) =>
  ingredients.map(i =>
    i.supplierItem?.commercialName
      ? `${i.supplierItem.commercialName.objectId} - ${i.supplierItem.commercialName.name || ""}`
      : ""
  )

const enterpriseLogoUrl = (e) =>
  e.logo?.publicId ? generateCloudinaryDownloadUrl(e.logo.publicId) : null

const enterpriseCellIcon = (enterprise, index) => {
  const url = enterpriseLogoUrl(enterprise)
  if (url) return <GridThumbnail key={index} src={url} alt={enterprise.name} />
  return (
    <Box
      key={index}
      title={enterprise.name}
      sx={{
        width: 25, height: 25, borderRadius: "50%",
        bgcolor: enterprise.color || "#9B9B9B",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontSize: 10, fontWeight: 700, color: "#fff", mr: "5px", flexShrink: 0,
      }}
    >
      {enterprise.name?.charAt(0).toUpperCase()}
    </Box>
  )
}

const enterpriseDropdownIcon = (enterprise) => {
  const url = enterpriseLogoUrl(enterprise)
  return url
    ? <img src={url} alt={enterprise.name} style={{ width: 20, height: 20, borderRadius: "50%", objectFit: "contain" }} />
    : <span style={{ width: 20, height: 20, borderRadius: "50%", background: enterprise.color || "#9B9B9B", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#fff", flexShrink: 0 }}>{enterprise.name?.charAt(0).toUpperCase()}</span>
}

const mapOptions = (option) => ({
  key: option.key,
  value: `${option.key} - ${option.label}`,
  label: `${option.key} - ${option.label}`,
  text: `${option.label}`
})

const mapTagOptions = (option) => ({
  key: option.key,
  value: option.label,
  label: option.label,
  text: option.label
})

const rawAdditionalFilters = [
  {
    key: "supplierItems",
    label: "Articles fournisseurs",
    dataType: "text",
    sortable: false,
    filter: { withSearch: true, filterable: true, type: "select", options: [], initialValue: "" },
  },
  {
    key: "commercialNames",
    label: "Ingrédient",
    dataType: "text",
    sortable: false,
    filter: { withSearch: true, filterable: true, type: "select", options: [], initialValue: "" },
  },
  {
    key: "internalTags",
    label: "Tag",
    dataType: "text",
    sortable: false,
    strict: true,
    filter: { withSearch: true, multiple: true, strict: true, filterable: true, type: "select", options: [], initialValue: [] },
  }
]

// --- styles ---

const useStyles = makeStyles({
  brandLogoContainer: { display: "flex", flexDirection: "row", flexWrap: "wrap" },
  fab: { position: "fixed", marginLeft: 20, bottom: 20, width: "fit-content" },
})

// --- component ---

const Recipes = () => {
  const dispatch = useDispatch()
  const classes = useStyles()

  const recipes = useSelector(getRecipes)
  const enterprises = useSelector(getOrganizationsEnterprisesSelector).filter(e => e.status !== "ARCHIVED")
  const productTypeOptions = useSelector(getProductTypeOptionsSelector)
  const commercialNamesOptions = useSelector(getCommercialNamesOptions)
  const supplierItemsOptions = useSelector(getSupplierItemsOptionsSelector)
  const productTagsOptions = useSelector(getProductTagsOptionsSelector)

  const [data, setData] = useState([])
  const [isCreating, setIsCreating] = useState(false)
  const [openOrderReportRDDialog, setOpenOrderReportRDDialog] = useState(false)
  const [additionalFilters, setAdditionalFilters] = useState(rawAdditionalFilters)
  const additionalFiltersFieldsAddedToRecipes = useRef(false)

  const columns = useMemo(() => [
    {
      key: "id",
      label: "ID",
      dataType: "text",
      sortable: false,
      filter: { filterable: true, type: "text", strict: true },
      width: 120
    },
    {
      key: "uniqueCode",
      label: "Code unique",
      dataType: "text",
      sortable: false,
      filter: { filterable: true, type: "text" },
      width: 100
    },
    {
      key: "name",
      label: "Nom technique",
      dataType: "text",
      sortable: false,
      filter: { filterable: true, type: "text", fullText: true }
    },
    {
      key: "commercialName",
      label: "Nom commercial",
      dataType: "text",
      sortable: false,
      filter: { filterable: true, type: "text", fullText: true }
    },
    {
      key: "brand",
      label: "Marque / Entreprise",
      dataType: "text",
      sortable: false,
      filter: {
        filterable: true,
        type: "select",
        options: [
          { type: "subheader", label: "Marques" },
          ...ACTIVE_KFC_BRANDS.map(brand => ({
            key: brand.name,
            value: brand.name,
            label: brand.label,
            icon: brand.image ? <img src={brand.image} alt={brand.label} style={{ width: 20, height: 20, objectFit: "contain" }} /> : null,
          })),
          { type: "subheader", label: "Entreprises" },
          ...enterprises.map(e => ({
            key: e.objectId,
            value: e.objectId,
            label: e.name,
            icon: enterpriseDropdownIcon(e),
          })),
        ],
        render: (value) => {
          const brand = getBrandBy("name", value)
          if (brand) return brand.label
          return enterprises.find(e => e.objectId === value)?.name ?? value
        }
      },
      render: (brands) => (
        <div className={classes.brandLogoContainer}>
          {brands && brands.split(",").filter(Boolean).map((value, index) => {
            const brand = getBrandBy("name", value)
            if (brand) return <GridThumbnail key={index} src={brand.image} alt={`${brand.shortname}_logo`} />
            const enterprise = enterprises.find(e => e.objectId === value)
            return enterprise ? enterpriseCellIcon(enterprise, index) : null
          })}
        </div>
      ),
      width: 220
    },
    {
      key: "type",
      label: "Type",
      dataType: "text",
      sortable: false,
      filter: {
        filterable: true,
        type: "select",
        options: productTypeOptions?.map(pt => ({ key: pt.value, value: pt.label, label: pt.label }))
      },
      width: 100
    },
    {
      key: "status",
      label: "Statut",
      dataType: "text",
      sortable: false,
      filter: {
        filterable: true,
        type: "select",
        options: RecipeStatus.map(s => ({ key: s.value, value: s.label, label: s.label }))
      },
      width: 100
    },
    {
      key: "state",
      label: "Etat",
      dataType: "text",
      sortable: false,
      filter: {
        filterable: true,
        type: "select",
        options: [
          { key: "active", value: "active", label: "Actif" },
          { key: "inactive", value: "inactive", label: "Inactif" },
        ]
      },
      render: (state) => <BooleanIcons checked={state === "active"} />,
      width: 100
    },
    {
      key: "updatedAt",
      label: "Date de modification",
      dataType: "date",
      type: "date",
      sortable: true,
      filter: { filterable: false, type: "date" },
      width: 130
    }
  ], [enterprises, productTypeOptions, classes])

  useEffect(() => {
    setData(recipes.map(recipe => ({
      id: recipe.objectId,
      uniqueCode: recipe.uniqueCode,
      name: recipe.name,
      commercialName: recipe.commercialName || "",
      brand: [...(recipe.brands || []), ...(recipe.enterprises || []).map(e => e.objectId)].join(","),
      type: getProductTypeLabelByProduct(recipe, productTypeOptions),
      status: getRecipeStatusToString(parseInt(recipe.status)),
      state: recipe.isActive === true ? "active" : "inactive",
      commercialNames: formatRecipeCommercialNames(recipe.ingredients),
      supplierItems: formatRecipeSupplierItems(recipe.ingredients),
      internalTags: formatRecipeInternalTags(recipe.internalTag),
      updatedAt: moment(recipe.updatedAt).format(TableDateFormat),
    })))
  }, [recipes])

  useEffect(() => {
    if (!enterprises.length) dispatch(loadEnterprises())
  }, [])

  useEffect(() => {
    const updatedFilters = additionalFilters.map(filter => ({
      ...filter,
      filter: {
        ...filter.filter,
        options: filter.key === "supplierItems" ? supplierItemsOptions?.map(mapOptions)
          : filter.key === "commercialNames" ? commercialNamesOptions?.map(mapOptions)
          : filter.key === "internalTags" ? productTagsOptions?.map(mapTagOptions)
          : filter.filter.options
      }
    }))
    setAdditionalFilters(updatedFilters)
  }, [commercialNamesOptions, supplierItemsOptions, productTagsOptions])

  const onOpenAdditionalFilters = () => {
    if (!additionalFiltersFieldsAddedToRecipes.current) {
      dispatch(refreshRecipesForAdditionalFilters())
      dispatch(loadAdditionalFiltersForRecipes())
      additionalFiltersFieldsAddedToRecipes.current = true
    }
  }

  const moreMenus = [
    { onClick: () => dispatch(showAdvancedSearch()), label: "Accéder à la recherche avancée" },
    { onClick: () => setOpenOrderReportRDDialog(v => !v), label: "Générer un rapport de commande R&D" },
  ]

  return (
    <>
      <DataGrid
        title="Produits internes"
        columns={columns}
        data={data}
        withFilters
        rowLabel="produits internes"
        menus={moreMenus}
        additionalFilters={additionalFilters}
        onRowClick={recipe => dispatch(showRecipeTab("general", recipe.id))}
        additionalFiltersPreCallback={onOpenAdditionalFilters}
        localStorageKey={RECIPE_DATAGRID_STORAGE}
      />
      <ReportOrderRDDialog
        open={openOrderReportRDDialog}
        onClose={() => setOpenOrderReportRDDialog(v => !v)}
      />
      <ProductResumeCreate
        onClose={() => setIsCreating(v => !v)}
        isCreating={isCreating}
        onSaveProductResume={values => dispatch(createProductResume(values))}
      />
      <div className={classes.fab}>
        <Fab color="primary" onClick={() => setIsCreating(v => !v)}>
          <Add />
        </Fab>
      </div>
    </>
  )
}

export default Recipes
